import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Star,
  ExternalLink,
  Building2,
  BarChart3,
  DollarSign,
  Percent,
  Activity,
  PieChart,
  Calendar,
  Loader2,
  ShieldCheck,
  Globe,
  Landmark,
  Briefcase,
  Download,
  FileText,
  Copy,
  Check,
  ShoppingCart
} from 'lucide-react';
import { markets, formatCurrency, formatMarketCap } from '../data/mockData';
import { stocksAPI, liquidityAPI, sanctionsAPI, intelligenceAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { MetricLabel } from '../components/MetricTooltip';
import { LiquidityScoreCard } from '../components/LiquidityScore';
import ScoringSection from '../components/ScoringSection';
import { SanctionsRiskCard } from '../components/SanctionsRisk';
import RealMetricsWidget from '../components/RealMetricsWidget';
import StyledMarkdown from '../components/StyledMarkdown';
import { ValoramapScoreHeader } from '../components/ValoramapScore';
import ValoramapScoreBasisBadge from '../components/ValoramapScoreBasisBadge';

const StockDetailPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  
  // State for price history and stock data
  const [priceHistory, setPriceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('1Y');
  const [stock, setStock] = useState(null);
  const [stockLoading, setStockLoading] = useState(true);
  const [liquidityData, setLiquidityData] = useState(null);
  const [sanctionsData, setSanctionsData] = useState(null);
  
  // State for Intelligence Report
  const [intelligenceReport, setIntelligenceReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  // State for affiliate code copy
  const [codeCopied, setCodeCopied] = useState(false);
  const [isinCopied, setIsinCopied] = useState(null); // Track which ISIN was copied
  
  // State for Market Access
  const [marketAccess, setMarketAccess] = useState(null);
  const [marketAccessLoading, setMarketAccessLoading] = useState(true);
  const [accessCountry, setAccessCountry] = useState('CH');
  
  // Vehicle type display mapping (2.4 fix)
  const vehicleTypeLabels = {
    'direct': { label: 'Achat direct', color: 'bg-[#00ff88]', textColor: 'text-[#0f0f23]' },
    'adr': { label: 'ADR', color: 'bg-blue-500', textColor: 'text-white' },
    'gdr': { label: 'GDR', color: 'bg-blue-400', textColor: 'text-white' },
    'etf': { label: 'ETF', color: 'bg-purple-500', textColor: 'text-white' },
    'etf_ucits': { label: 'ETF UCITS', color: 'bg-purple-500', textColor: 'text-white' },
    'etf_us': { label: 'ETF US', color: 'bg-purple-400', textColor: 'text-white' },
    'cef': { label: 'Fonds fermé (CEF)', color: 'bg-amber-500', textColor: 'text-white' },
    'cross_listing': { label: 'Cotation européenne', color: 'bg-cyan-500', textColor: 'text-white' },
    'mutual_fund': { label: 'Fonds actif', color: 'bg-orange-500', textColor: 'text-white' },
    'local_broker': { label: 'Courtier local', color: 'bg-gray-500', textColor: 'text-white' }
  };

  // Cascade level labels and colors
  const cascadeLevelInfo = {
    1: { label: 'Achat direct', color: 'bg-[#00ff88]', badge: '🟢' },
    2: { label: 'ADR disponible', color: 'bg-[#00ff88]', badge: '🟢' },
    3: { label: 'ETF contenant l\'action', color: 'bg-[#ffa502]', badge: '🟡' },
    4: { label: 'Via fonds actif', color: 'bg-[#ff8c00]', badge: '🟠' },
    5: { label: 'Accès limité', color: 'bg-[#ff8c00]', badge: '🟠' },
    6: { label: 'Non accessible', color: 'bg-[#ff4757]', badge: '🔴' }
  };

  // Handle copy referral code
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText('ku79q0');
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle copy ISIN
  const handleCopyIsin = async (isin) => {
    try {
      await navigator.clipboard.writeText(isin);
      setIsinCopied(isin);
      setTimeout(() => setIsinCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy ISIN:', err);
    }
  };

  // Get broker links based on vehicle and country
  const getBrokerLinks = (vehicle, country) => {
    const isEuropeanExchange = ['XETRA', 'LSE', 'AMS', 'EPA', 'SWX', 'MIL', 'BME'].some(
      ex => vehicle.exchange?.toUpperCase().includes(ex)
    );
    
    const links = [
      // Affiliate links first
      { name: 'IBKR', url: 'https://ibkr.com/referral/alain930', affiliate: true },
      { name: 'Degiro', url: 'https://www.degiro.fr/parrainage/commencez-a-investir?id=F11499CA&utm_source=mgm', affiliate: true },
      { name: 'Swissquote', url: 'https://swissquote.ch', code: 'ku79q0', affiliate: true },
    ];
    
    // Add Trade Republic and Boursorama for FR/BE on European exchanges
    if ((country === 'FR' || country === 'BE') && isEuropeanExchange) {
      links.push({ name: 'Trade Republic', url: 'https://traderepublic.com', affiliate: false });
      links.push({ name: 'Boursorama', url: 'https://boursorama.com', affiliate: false });
    }
    
    return links;
  };
  
  // Parse market and symbol from URL (format: MARKET-SYMBOL)
  // Handle cases with multiple dashes (e.g., JK-ABC-DEF -> market=JK, symbol=ABC-DEF)
  const parseSymbol = (urlSymbol) => {
    if (!urlSymbol) return [null, null];
    const firstDash = urlSymbol.indexOf('-');
    if (firstDash === -1) return [null, urlSymbol];
    return [urlSymbol.substring(0, firstDash), urlSymbol.substring(firstDash + 1)];
  };
  const [marketCode, stockSymbol] = parseSymbol(symbol);
  
  const market = markets.find(m => m.code === marketCode);

  // Reset all states when ticker changes (critical fix for stale data bug)
  // This runs synchronously before any fetch effects
  useEffect(() => {
    // Reset all stock-related states to prevent showing stale data
    setStock(null);
    setStockLoading(true);
    setPriceHistory([]);
    setHistoryLoading(true);
    setLiquidityData(null);
    setSanctionsData(null);
    setMarketAccess(null);
    setMarketAccessLoading(true);
    setIntelligenceReport(null);
    setReportLoading(false);
  }, [symbol]); // Trigger on URL symbol change

  // Fetch stock data from API
  useEffect(() => {
    const fetchStock = async () => {
      if (!marketCode || !stockSymbol) return;
      
      setStockLoading(true);
      try {
        const data = await stocksAPI.getStock(marketCode, stockSymbol);
        setStock(data);
      } catch (error) {
        console.error('Failed to fetch stock:', error);
        // No fallback - stock not found in API
        setStock(null);
      } finally {
        setStockLoading(false);
      }
    };
    
    fetchStock();
  }, [marketCode, stockSymbol]);

  // Fetch liquidity and sanctions data
  useEffect(() => {
    const fetchRiskData = async () => {
      if (!marketCode || !stockSymbol) return;
      
      // Fetch liquidity data
      try {
        const liqData = await liquidityAPI.getLiquidity(marketCode, stockSymbol);
        setLiquidityData(liqData);
      } catch (error) {
        setLiquidityData(null);
      }
      
      // Fetch sanctions data
      try {
        const sanctData = await sanctionsAPI.getSanctions(marketCode, stockSymbol);
        setSanctionsData(sanctData);
      } catch (error) {
        setSanctionsData(null);
      }
    };
    
    fetchRiskData();
  }, [marketCode, stockSymbol]);

  // Fetch price history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!marketCode || !stockSymbol) return;
      
      setHistoryLoading(true);
      try {
        const data = await stocksAPI.getStockHistory(marketCode, stockSymbol, selectedPeriod);
        if (data.history && data.history.length > 0) {
          // Format data for chart
          const formattedData = data.history.map(item => ({
            date: item.date,
            price: item.close,
            volume: item.volume
          }));
          setPriceHistory(formattedData);
        } else {
          setPriceHistory([]);
        }
      } catch (error) {
        console.error('Failed to fetch price history:', error);
        setPriceHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };
    
    fetchHistory();
  }, [marketCode, stockSymbol, selectedPeriod]);

  // Fetch market access data
  useEffect(() => {
    const fetchMarketAccess = async () => {
      if (!stockSymbol) return;
      
      setMarketAccessLoading(true);
      try {
        const data = await stocksAPI.getMarketAccess(stockSymbol, accessCountry);
        setMarketAccess(data);
      } catch (error) {
        console.error('Failed to fetch market access:', error);
        setMarketAccess(null);
      } finally {
        setMarketAccessLoading(false);
      }
    };
    
    fetchMarketAccess();
  }, [stockSymbol, accessCountry]);

  // Fetch intelligence report
  useEffect(() => {
    const fetchIntelligenceReport = async () => {
      if (!marketCode || !stockSymbol) return;
      
      setReportLoading(true);
      try {
        const data = await intelligenceAPI.getReport(marketCode, stockSymbol);
        setIntelligenceReport(data);
      } catch (error) {
        setIntelligenceReport(null);
      } finally {
        setReportLoading(false);
      }
    };
    
    fetchIntelligenceReport();
  }, [marketCode, stockSymbol]);

  // Handle PDF download
  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const blob = await intelligenceAPI.downloadPDF(marketCode, stockSymbol);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${stockSymbol}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (stockLoading) {
    return (
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Action non trouvée</h1>
          <p className="text-gray-400 mb-6">L'action que vous recherchez n'existe pas.</p>
          <Button onClick={() => navigate('/screener')} className="bg-emerald-500 hover:bg-emerald-600">
            Retour au screener
          </Button>
        </div>
      </div>
    );
  }

  // Calculate price change from history
  const priceChange = priceHistory.length > 1 
    ? ((priceHistory[priceHistory.length - 1]?.price - priceHistory[0]?.price) / priceHistory[0]?.price * 100).toFixed(2)
    : 0;

  // Helper function to detect aberrant P/B values
  const formatPBRatio = (value) => {
    if (value === null || value === undefined) return '—';
    if (value > 50 || value < -50) return '—';
    return value.toFixed(2);
  };

  // Helper function to detect aberrant P/E values
  const formatPERatio = (value) => {
    if (value === null || value === undefined) return '—';
    if (value > 200 || value < -200) return '—';
    return value.toFixed(2);
  };

  // Fundamental metrics with tooltip keys
  const fundamentalMetrics = [
    { label: 'P/E Ratio', value: formatPERatio(stock.pe_ratio ?? stock.pe), metric: 'pe', icon: BarChart3 },
    { label: 'P/B Ratio', value: formatPBRatio(stock.pb_ratio ?? stock.pb), metric: 'pb', icon: DollarSign },
    { label: 'ROE', value: stock.roe == null ? '—' : `${stock.roe.toFixed(1)}%`, metric: 'roe', icon: Percent },
    { label: 'Market Cap', value: formatMarketCap(stock.market_cap ?? stock.marketCap, stock.currency), metric: 'marketCap', icon: PieChart },
  ];

  // Mock financial data
  const financialData = {
    revenue: [120, 145, 168, 192, 215],
    netIncome: [18, 22, 28, 32, 38],
    years: ['2021', '2022', '2023', '2024', '2025E']
  };

  return (
    <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        {/* Stock Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <Badge variant="outline" className="border-white/20 text-gray-300">
                {market?.flag} {stock.market}
              </Badge>
              <Badge className="bg-white/10 text-gray-300 border-0">
                {stock.sector}
              </Badge>
              {/* Cascade level badge */}
              {marketAccess?.cascade_level && (
                <Badge className={`${
                  marketAccess.cascade_level <= 2 ? 'bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/30' :
                  marketAccess.cascade_level === 3 ? 'bg-[#ffa502]/20 text-[#ffa502] border-[#ffa502]/30' :
                  'bg-[#ff8c00]/20 text-[#ff8c00] border-[#ff8c00]/30'
                } border`}>
                  {cascadeLevelInfo[marketAccess.cascade_level]?.badge} Niveau {marketAccess.cascade_level}
                </Badge>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1 flex items-center gap-4">
              {stock.symbol}
              {/* VALORAMAP Score Badge */}
              {stock.valoramap_score !== null && stock.valoramap_score !== undefined && (
                <ValoramapScoreHeader score={stock.valoramap_score} />
              )}
              {/* METHODOLOGY §4.6.3 / §4.7.1 — visual indicator for non-full basis */}
              <ValoramapScoreBasisBadge basis={stock.valoramap_score_basis} />
            </h1>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <p className="text-xl text-gray-400">{stock.name}</p>
              </div>
              {/* Exchange, Currency, ISIN info (2.2) */}
              <div className="flex items-center gap-2 text-sm text-[#4a4a5e]">
                <span>{stock.exchange || market?.name || stock.market}</span>
                <span>·</span>
                <span>{stock.currency}</span>
                {stock.isin && (
                  <>
                    <span>·</span>
                    <button
                      onClick={() => handleCopyIsin(stock.isin)}
                      className="flex items-center gap-1 hover:text-[#00ff88] transition-colors cursor-pointer"
                      title="Cliquer pour copier l'ISIN"
                    >
                      <span>ISIN: {stock.isin}</span>
                      {isinCopied === stock.isin ? (
                        <Check className="w-3 h-3 text-[#00ff88]" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                  </>
                )}
              </div>
              {/* Last update date */}
              {(stock.updated_at || stock.last_updated) && (
                <div className="text-xs text-[#4a4a5e] mt-1">
                  Données mises à jour le : {new Date(stock.updated_at || stock.last_updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-start lg:items-end gap-2">
            <div className="text-3xl sm:text-4xl font-bold text-white">
              {formatCurrency(stock.price, stock.currency)}
            </div>
            <div className={`flex items-center gap-2 text-lg ${
              stock.change >= 0 ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {stock.change >= 0 ? (
                <TrendingUp className="w-5 h-5" />
              ) : (
                <TrendingDown className="w-5 h-5" />
              )}
              <span className="font-medium">
                {stock.change >= 0 ? '+' : ''}{stock.change}%
              </span>
              <span className="text-gray-500 text-sm">24h</span>
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" className="border-white/10 text-gray-300 hover:bg-white/5">
                <Star className="w-4 h-4 mr-2" />
                Watchlist
              </Button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {fundamentalMetrics.map((m, index) => (
            <Card key={index} className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                  <m.icon className="w-4 h-4" />
                  <MetricLabel metric={m.metric} label={m.label} />
                </div>
                <div className="text-2xl font-bold text-white">{m.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="financials" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Fondamentaux
            </TabsTrigger>
            <TabsTrigger value="analysis" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
              Analyse
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Risk Assessment Row - Sanctions & Liquidity */}
            <div className="grid md:grid-cols-2 gap-6">
              <SanctionsRiskCard data={sanctionsData} />
              <LiquidityScoreCard data={liquidityData} />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Price Chart */}
              <Card className="lg:col-span-2 bg-white/5 border-white/10">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    Évolution du prix
                    {priceHistory.length > 0 && (
                      <span className={`text-sm font-normal ml-2 ${parseFloat(priceChange) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {parseFloat(priceChange) >= 0 ? '+' : ''}{priceChange}%
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex gap-1">
                    {['1M', '3M', '6M', '1Y', '2Y'].map((period) => (
                      <Button
                        key={period}
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedPeriod(period)}
                        className={`text-xs px-2 py-1 h-7 ${
                          selectedPeriod === period 
                            ? 'bg-emerald-500 text-white' 
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {period}
                      </Button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                    </div>
                  ) : priceHistory.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={priceHistory}>
                          <defs>
                            <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#6b7280"
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getDate()}/${date.getMonth() + 1}`;
                            }}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            stroke="#6b7280"
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            domain={['auto', 'auto']}
                            tickFormatter={(value) => value.toLocaleString()}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#1f2937', 
                              border: '1px solid #374151',
                              borderRadius: '8px',
                              color: '#fff'
                            }}
                            labelFormatter={(value) => new Date(value).toLocaleDateString('fr-FR')}
                            formatter={(value) => [value.toLocaleString() + ' ' + stock.currency, 'Prix']}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="price" 
                            stroke="#10b981" 
                            strokeWidth={2}
                            fillOpacity={1} 
                            fill="url(#colorPrice)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center border border-dashed border-white/20 rounded-lg">
                      <div className="text-center">
                        <BarChart3 className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                        <p className="text-gray-500">Données historiques non disponibles</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Catalyst Signal Banner */}
              {marketAccess?.catalyst_signal && (
                <Card className={`lg:col-span-3 border-l-4 ${
                  marketAccess.catalyst_signal.urgency === 'imminent' 
                    ? 'bg-red-500/10 border-red-500 border-white/10' 
                    : marketAccess.catalyst_signal.urgency === 'approaching'
                    ? 'bg-amber-500/10 border-amber-500 border-white/10'
                    : 'bg-white/5 border-emerald-500 border-white/10'
                }`}>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      {/* Main signal message */}
                      <p className={`font-medium ${
                        marketAccess.catalyst_signal.urgency === 'imminent'
                          ? 'text-red-400'
                          : marketAccess.catalyst_signal.urgency === 'approaching'
                          ? 'text-amber-400'
                          : 'text-emerald-400'
                      }`}>
                        {marketAccess.catalyst_signal.urgency === 'imminent' && '⏰ '}
                        {marketAccess.catalyst_signal.urgency === 'approaching' && '⚠️ '}
                        {marketAccess.catalyst_signal.urgency === 'distant' && '📈 '}
                        {marketAccess.catalyst_signal.event}
                        {marketAccess.catalyst_signal.days_until !== undefined && (
                          <span> dans {marketAccess.catalyst_signal.days_until} jours</span>
                        )}
                        {marketAccess.catalyst_signal.urgency !== 'distant' && marketAccess.catalyst_signal.context && (
                          <span className="text-gray-400 font-normal"> — {marketAccess.catalyst_signal.context}</span>
                        )}
                      </p>

                      {/* Eligibility info */}
                      {marketAccess.catalyst_signal.ticker_eligible && (
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="text-emerald-400">
                            ✅ {stockSymbol} fait partie des actions éligibles à la reclassification FTSE
                          </span>
                          {marketAccess.catalyst_signal.impact && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                              {marketAccess.catalyst_signal.impact}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Scoring Section - Qualité Comptable & Santé Financière */}
              <ScoringSection stock={stock} />

              {/* Unified Investment Section - Comment investir ? (2.1 Refonte) */}
              <Card className="lg:col-span-3 bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl">
                <CardHeader className="flex flex-row items-center justify-between border-b border-[#2a2a3e]">
                  <CardTitle className="text-white flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-[#00ff88]" />
                    Comment investir ?
                  </CardTitle>
                  <div className="flex gap-1">
                    {[
                      { code: 'CH', label: '🇨🇭 CH' },
                      { code: 'FR', label: '🇫🇷 FR' },
                      { code: 'BE', label: '🇧🇪 BE' },
                      { code: 'CA', label: '🇨🇦 CA' }
                    ].map((country) => (
                      <Button
                        key={country.code}
                        variant="ghost"
                        size="sm"
                        onClick={() => setAccessCountry(country.code)}
                        className={`text-xs px-3 py-1 h-7 ${
                          accessCountry === country.code 
                            ? 'bg-[#00ff88] text-[#0f0f23]' 
                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {country.label}
                      </Button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {marketAccessLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 text-[#00ff88] animate-spin" />
                    </div>
                  ) : marketAccess?.cascade && marketAccess.cascade.length > 0 ? (
                    <div className="space-y-6">
                      {/* Stock info header */}
                      {(marketAccess.name || marketAccess.exchange_display || marketAccess.currency) && (
                        <div className="p-3 rounded-lg bg-[#0f0f23] border border-[#2a2a3e]">
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            {marketAccess.exchange_display && <span>{marketAccess.exchange_display}</span>}
                            {marketAccess.currency && (
                              <>
                                <span>·</span>
                                <span className="text-[#00ff88]">{marketAccess.currency}</span>
                              </>
                            )}
                            {marketAccess.isin && (
                              <>
                                <span>·</span>
                                <button
                                  onClick={() => handleCopyIsin(marketAccess.isin)}
                                  className="flex items-center gap-1 hover:text-[#00ff88] transition-colors"
                                  title="ISIN copié — collez dans votre courtier"
                                >
                                  <span>ISIN: {marketAccess.isin}</span>
                                  {isinCopied === marketAccess.isin ? (
                                    <Check className="w-3 h-3 text-[#00ff88]" />
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Render each cascade level */}
                      {marketAccess.cascade.map((level, levelIndex) => {
                        const badgeColor = level.badge_color === 'green' ? 'bg-[#00ff88]' :
                                          level.badge_color === 'yellow' ? 'bg-[#ffa502]' :
                                          level.badge_color === 'orange' ? 'bg-[#ff8c00]' :
                                          level.badge_color === 'red' ? 'bg-[#ff4757]' : 'bg-gray-500';
                        const badgeEmoji = level.badge_color === 'green' ? '🟢' :
                                          level.badge_color === 'yellow' ? '🟡' :
                                          level.badge_color === 'orange' ? '🟠' : '🔴';
                        const borderColor = level.badge_color === 'green' ? 'border-[#00ff88]/30' :
                                           level.badge_color === 'yellow' ? 'border-[#ffa502]/30' :
                                           level.badge_color === 'orange' ? 'border-[#ff8c00]/30' : 'border-[#ff4757]/30';
                        const bgColor = level.badge_color === 'green' ? 'bg-[#00ff88]/10' :
                                       level.badge_color === 'yellow' ? 'bg-[#ffa502]/10' :
                                       level.badge_color === 'orange' ? 'bg-[#ff8c00]/10' : 'bg-[#ff4757]/10';

                        return (
                          <div key={levelIndex} className="space-y-3">
                            {/* Level header */}
                            <div className={`flex items-center gap-3 p-3 rounded-lg ${bgColor} border ${borderColor}`}>
                              <div className={`px-3 py-1 rounded-lg text-sm font-bold ${badgeColor} text-[#0f0f23]`}>
                                {badgeEmoji} Niveau {level.level}
                              </div>
                              <span className="text-white font-medium">{level.label}</span>
                            </div>

                            {/* Vehicles for this level */}
                            {level.vehicles && level.vehicles.map((vehicle, vIndex) => {
                              // Get broker links based on vehicle.brokers array from API
                              const brokerLinks = [];
                              if (vehicle.brokers && Array.isArray(vehicle.brokers)) {
                                vehicle.brokers.forEach(brokerName => {
                                  if (brokerName === 'IBKR') {
                                    brokerLinks.push({ name: 'IBKR', url: 'https://ibkr.com/referral/alain930', affiliate: true });
                                  } else if (brokerName === 'Degiro') {
                                    brokerLinks.push({ name: 'Degiro', url: 'https://www.degiro.fr/parrainage/commencez-a-investir?id=F11499CA&utm_source=mgm', affiliate: true });
                                  } else if (brokerName === 'Swissquote') {
                                    brokerLinks.push({ name: 'Swissquote', url: 'https://swissquote.ch', code: 'ku79q0', affiliate: true });
                                  } else if (brokerName === 'Trade Republic') {
                                    brokerLinks.push({ name: 'Trade Republic', url: 'https://traderepublic.com', affiliate: false });
                                  } else if (brokerName === 'Boursorama') {
                                    brokerLinks.push({ name: 'Boursorama', url: 'https://boursorama.com', affiliate: false });
                                  }
                                });
                              }

                              return (
                                <div 
                                  key={vehicle.ticker || `v-${vIndex}`}
                                  className="p-4 rounded-lg bg-[#0f0f23] border border-[#2a2a3e] hover:border-[#00ff88]/30 transition-colors"
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                    <div className="flex-1">
                                      {/* Main line: Ticker — Type — TER — Currency */}
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="text-white font-bold">{vehicle.ticker}</span>
                                        <span className="text-gray-500">—</span>
                                        <span className="text-xs px-2 py-0.5 rounded font-medium bg-purple-500 text-white">
                                          {vehicle.type_display || vehicle.type}
                                        </span>
                                        {vehicle.ter && (
                                          <>
                                            <span className="text-gray-500">—</span>
                                            <span className="text-[#00ff88] font-medium">
                                              TER {(vehicle.ter * 100).toFixed(2)}%
                                            </span>
                                          </>
                                        )}
                                        {vehicle.currency && (
                                          <>
                                            <span className="text-gray-500">—</span>
                                            <span className="text-[#00ff88] font-medium">{vehicle.currency}</span>
                                          </>
                                        )}
                                      </div>
                                      {/* Name */}
                                      <p className="text-gray-400 text-sm">{vehicle.name}</p>
                                      {/* Exchange */}
                                      {vehicle.exchange_display && (
                                        <p className="text-sm text-gray-500">{vehicle.exchange_display}</p>
                                      )}
                                      {/* Weight in ETF */}
                                      {vehicle.weight_pct && (
                                        <p className="text-sm text-purple-400">
                                          Poids {stockSymbol} dans l'ETF : {vehicle.weight_pct}%
                                        </p>
                                      )}
                                      {/* ISIN if available */}
                                      {vehicle.isin && (
                                        <button
                                          onClick={() => handleCopyIsin(vehicle.isin)}
                                          className="flex items-center gap-1 text-sm text-[#4a4a5e] hover:text-[#00ff88] transition-colors mt-1"
                                          title="ISIN copié — collez dans votre courtier"
                                        >
                                          <span>ISIN: {vehicle.isin}</span>
                                          {isinCopied === vehicle.isin ? (
                                            <Check className="w-3 h-3 text-[#00ff88]" />
                                          ) : (
                                            <Copy className="w-3 h-3" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                    {/* Broker buttons */}
                                    <div className="flex flex-wrap items-center gap-2">
                                      {brokerLinks.map((broker, idx) => (
                                        <a
                                          key={idx}
                                          href={broker.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                                            broker.affiliate 
                                              ? 'bg-[#00ff88]/20 text-[#00ff88] hover:bg-[#00ff88]/30 border border-[#00ff88]/30' 
                                              : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/10'
                                          }`}
                                          title={broker.code ? `Code parrainage: ${broker.code}` : undefined}
                                        >
                                          {broker.code ? `${broker.name} (${broker.code})` : broker.name}
                                          <ExternalLink className="w-3 h-3" />
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}

                      {/* Catalyst info */}
                      {marketAccess.catalyst && (
                        <div className="p-4 rounded-lg bg-[#ffa502]/10 border border-[#ffa502]/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Activity className="w-4 h-4 text-[#ffa502]" />
                            <span className="text-[#ffa502] font-semibold">Catalyseur</span>
                          </div>
                          <p className="text-white">{marketAccess.catalyst}</p>
                          {marketAccess.catalyst_text && (
                            <p className="text-gray-400 text-sm mt-1">{marketAccess.catalyst_text}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#4a4a5e]">
                      <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune méthode d'accès trouvée pour les investisseurs {accessCountry}</p>
                    </div>
                  )}

                  {/* Sub-section: Ouvrir un compte courtier */}
                  <div>
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Landmark className="w-4 h-4 text-[#00ff88]" />
                      Ouvrir un compte courtier
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Interactive Brokers */}
                      <div className="bg-[#0f0f23] border border-[#2a2a3e] rounded-xl p-5 flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-[#D70000] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">IB</span>
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">Interactive Brokers</h4>
                            <p className="text-[#4a4a5e] text-xs">Accès mondial • Frais réduits</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm mb-4 flex-grow">
                          Accès à plus de 150 marchés dans le monde avec des frais parmi les plus bas du secteur.
                        </p>
                        <a
                          href="https://ibkr.com/referral/alain930"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full"
                        >
                          <Button className="w-full bg-[#00ff88] text-[#0f0f23] font-semibold rounded-lg hover:opacity-90 transition-opacity">
                            Ouvrir un compte IBKR
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      </div>

                      {/* Degiro */}
                      <div className="bg-[#0f0f23] border border-[#2a2a3e] rounded-xl p-5 flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-[#0066CC] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">DG</span>
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">Degiro</h4>
                            <p className="text-[#4a4a5e] text-xs">Simple • Accessible</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm mb-4 flex-grow">
                          Plateforme européenne populaire avec une interface simple et des frais compétitifs.
                        </p>
                        <a
                          href="https://www.degiro.fr/parrainage/commencez-a-investir?id=F11499CA&utm_source=mgm"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full"
                        >
                          <Button className="w-full bg-[#00ff88] text-[#0f0f23] font-semibold rounded-lg hover:opacity-90 transition-opacity">
                            Ouvrir un compte Degiro
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      </div>

                      {/* Swissquote */}
                      <div className="bg-[#0f0f23] border border-[#2a2a3e] rounded-xl p-5 flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-[#E30613] rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-sm">SQ</span>
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">Swissquote</h4>
                            <p className="text-[#4a4a5e] text-xs">Suisse • Sécurisé</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm mb-3 flex-grow">
                          Banque en ligne suisse réputée pour sa sécurité et sa fiabilité.
                        </p>
                        <div className="flex items-center gap-2 mb-3 p-2 bg-[#1a1a2e] rounded-lg border border-[#2a2a3e]">
                          <span className="text-[#4a4a5e] text-sm">Code parrainage :</span>
                          <span className="text-[#00ff88] font-mono font-bold">ku79q0</span>
                          <button
                            onClick={handleCopyCode}
                            className="ml-auto p-1.5 hover:bg-[#2a2a3e] rounded transition-colors"
                            title="Copier le code"
                          >
                            {codeCopied ? (
                              <Check className="w-4 h-4 text-[#00ff88]" />
                            ) : (
                              <Copy className="w-4 h-4 text-[#4a4a5e]" />
                            )}
                          </button>
                        </div>
                        <a
                          href="https://swissquote.ch"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full"
                        >
                          <Button className="w-full bg-[#00ff88] text-[#0f0f23] font-semibold rounded-lg hover:opacity-90 transition-opacity">
                            Visiter Swissquote
                            <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Single disclaimer */}
                  <p className="text-[#4a4a5e] text-sm mt-6 text-center">
                    Ces liens sont des liens d'affiliation. VALORAMAP peut recevoir une commission sans coût supplémentaire pour vous.
                  </p>
                </CardContent>
              </Card>

              {/* Company Info */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-400" />
                    Informations
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-400">Marché</div>
                    <div className="text-white">{market?.flag} {market?.name} ({market?.exchange})</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Secteur</div>
                    <div className="text-white">{stock.sector}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Devise</div>
                    <div className="text-white">{stock.currency}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Volume 24h</div>
                    <div className="text-white">{stock.volume == null ? '—' : stock.volume.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Croissance GDP ({market?.name})</div>
                    <div className="text-emerald-400 font-medium">{market?.gdpGrowth}</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="financials" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Revenue Chart */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Chiffre d'affaires (en M)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {financialData.years.map((year, index) => (
                      <div key={year} className="flex items-center gap-3">
                        <span className="text-sm text-gray-400 w-12">{year}</span>
                        <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full"
                            style={{ width: `${(financialData.revenue[index] / 215) * 100}%` }}
                          />
                        </div>
                        <span className="text-white font-medium w-16 text-right">
                          {financialData.revenue[index]}M
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Net Income Chart */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Résultat net (en M)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {financialData.years.map((year, index) => (
                      <div key={year} className="flex items-center gap-3">
                        <span className="text-sm text-gray-400 w-12">{year}</span>
                        <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-teal-500 rounded-full"
                            style={{ width: `${(financialData.netIncome[index] / 38) * 100}%` }}
                          />
                        </div>
                        <span className="text-white font-medium w-16 text-right">
                          {financialData.netIncome[index]}M
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Metrics */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Ratios clés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">
                      <MetricLabel metric="debtEquity" label="Debt/Equity" />
                    </div>
                    <div className="text-xl font-bold text-white">0.42</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">
                      <MetricLabel metric="currentRatio" label="Current Ratio" />
                    </div>
                    <div className="text-xl font-bold text-white">1.85</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">
                      <MetricLabel metric="fcfYield" label="FCF Yield" />
                    </div>
                    <div className="text-xl font-bold text-emerald-400">8.2%</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">
                      <MetricLabel metric="dividendYield" label="Dividend Yield" />
                    </div>
                    <div className="text-xl font-bold text-white">2.4%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analysis" className="space-y-6">
            {/* Scoring Section — Altman Z'', Piotroski F, Beneish M (lecture directe Famille A §5.4) */}
            <ScoringSection stock={stock} />

            {/* Real Metrics Widget */}
            <RealMetricsWidget market={marketCode} symbol={stockSymbol} />

            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                  Prochains événements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="text-white font-medium">Publication résultats Q1 2026</div>
                      <div className="text-sm text-gray-400">Estimation BPA: 0.45 {stock.currency}</div>
                    </div>
                    <Badge variant="outline" className="border-white/20 text-gray-300">15 Mar 2026</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="text-white font-medium">Dividende</div>
                      <div className="text-sm text-gray-400">Rendement estimé: 2.4%</div>
                    </div>
                    <Badge variant="outline" className="border-white/20 text-gray-300">28 Avr 2026</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Intelligence Report Section */}
            <Card className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-xl">
              <CardHeader className="border-b border-[#2a2a3e]">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#00ff88]" />
                    Rapport d'Analyse
                  </CardTitle>
                  {intelligenceReport && (
                    <Button
                      onClick={handleDownloadPDF}
                      disabled={downloadingPDF}
                      variant="outline"
                      size="sm"
                      className="border-[#00ff88]/30 text-[#00ff88] hover:bg-[#00ff88]/10"
                    >
                      {downloadingPDF ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Télécharger PDF
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {reportLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <Loader2 className="w-8 h-8 text-[#00ff88] animate-spin" />
                  </div>
                ) : intelligenceReport?.report_markdown ? (
                  <StyledMarkdown content={intelligenceReport.report_markdown} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-[#4a4a5e]">
                    <FileText className="w-12 h-12 mb-3 opacity-50" />
                    <p>Rapport d'analyse non disponible pour cette action</p>
                    <p className="text-sm mt-2">
                      Les rapports sont générés pour les actions les plus suivies
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StockDetailPage;
